import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode
} from 'react'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useFeatureTogglesStore } from 'stores/featureTogglesStore'
import { isImpersonating } from 'utils/impersonation'
import type {
  OnboardingV3ContextType,
  OnboardingV3State
} from './onboarding-v3.types'

const ONBOARDING_V3_STORAGE_KEY = 'arvore_onboarding_v3_state'
const PAGES_REQUIRED = 5

const OnboardingV3Context = createContext<OnboardingV3ContextType>(
  {} as OnboardingV3ContextType
)

const getDefaultState = (): OnboardingV3State => ({
  currentStep: 'welcome',
  pagesRead: 0,
  isVisible: true,
  hasSeenCompletion: false,
  showChecklistHighlight: true,
  showFloatingButton: false,
  checklistItems: {
    interests: false,
    reading: false,
    conquests: false,
    tree: false
  }
})

const getInitialState = (): OnboardingV3State => {
  const stored = localStorage.getItem(ONBOARDING_V3_STORAGE_KEY)
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      if (parsed && typeof parsed === 'object') {
        return {
          ...getDefaultState(),
          ...parsed
        }
      }
    } catch (error) {
      if (error) {
        return getDefaultState()
      }
    }
  }

  return getDefaultState()
}

type OnboardingV3ProviderProps = {
  children: ReactNode
}

const OnboardingV3Provider = ({ children }: OnboardingV3ProviderProps) => {
  const { user } = useUserInfo()
  const { featureToggles } = useFeatureTogglesStore()
  const isBiblion = featureToggles?.biblionGeneral === true
  const [state, setState] = useState<OnboardingV3State>(getInitialState)

  useEffect(() => {
    localStorage.setItem(ONBOARDING_V3_STORAGE_KEY, JSON.stringify(state))
  }, [state])

  const shouldShowOnboarding =
    user?.featureToggles?.onboardingAlunoV3 === true &&
    user?.reader?.canAccessOnboarding === true &&
    state.currentStep !== 'dismissed' &&
    !isBiblion &&
    !isImpersonating()

  useEffect(() => {
    if (shouldShowOnboarding) {
      setState(prev => {
        if (prev.showFloatingButton) return prev
        return {
          ...prev,
          showFloatingButton: true
        }
      })
    }
  }, [shouldShowOnboarding])

  const goToNextStep = useCallback(() => {
    setState(prev => {
      if (prev.currentStep === 'welcome') {
        return { ...prev, currentStep: 'footer' }
      }
      if (prev.currentStep === 'footer') {
        return {
          ...prev,
          currentStep: 'reading',
          showChecklistHighlight: true,
          showFloatingButton: true
        }
      }
      return prev
    })
  }, [])

  const goToPreviousStep = useCallback(() => {
    setState(prev => {
      if (prev.currentStep === 'footer') {
        return { ...prev, currentStep: 'welcome' }
      }
      return prev
    })
  }, [])

  const dismissOnboarding = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: 'dismissed',
      isVisible: false
    }))
  }, [])

  const incrementPagesRead = useCallback((pages: number) => {
    setState(prev => {
      const newPagesRead = Math.min(prev.pagesRead + pages, PAGES_REQUIRED)
      const shouldComplete =
        newPagesRead >= PAGES_REQUIRED && prev.currentStep === 'reading'

      return {
        ...prev,
        pagesRead: newPagesRead,
        currentStep: shouldComplete ? 'completed' : prev.currentStep,
        hasSeenCompletion: shouldComplete ? false : prev.hasSeenCompletion,
        checklistItems: {
          ...prev.checklistItems,
          reading: shouldComplete ? true : prev.checklistItems.reading
        }
      }
    })
  }, [])

  const showCompletionModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      hasSeenCompletion: true,
      isVisible: true
    }))
  }, [])

  const hideCompletionModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isVisible: false
    }))
  }, [])

  const hideChecklistHighlight = useCallback(() => {
    setState(prev => ({
      ...prev,
      showChecklistHighlight: false
    }))
  }, [])

  const [showFinalModal, setShowFinalModal] = useState(false)
  const [openModalCount, setOpenModalCount] = useState(0)

  const registerModalOpen = useCallback(() => {
    setOpenModalCount(prev => prev + 1)
  }, [])

  const registerModalClosed = useCallback(() => {
    setOpenModalCount(prev => Math.max(0, prev - 1))
  }, [])

  return (
    <OnboardingV3Context.Provider
      value={{
        state,
        goToNextStep,
        goToPreviousStep,
        dismissOnboarding,
        incrementPagesRead,
        showCompletionModal,
        hideCompletionModal,
        hideChecklistHighlight,
        showFinalModal,
        setShowFinalModal,
        shouldShowOnboarding,
        openModalCount,
        registerModalOpen,
        registerModalClosed
      }}
    >
      {children}
    </OnboardingV3Context.Provider>
  )
}

export { OnboardingV3Provider, OnboardingV3Context }
